import { useMemo, useCallback } from 'react'
import { useRouteLoaderData, useLoaderData, useParams, useNavigate } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditNoteIcon from '@mui/icons-material/EditNote'
import WarningIcon from '@mui/icons-material/Warning'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridInitialState, DataGridPremiumProps } from '@mui/x-data-grid-premium'

import {
  LoaderData,
  OptionType,
  Options,
  ParticipantOptionStatus,
  ContributorRole,
} from '../../../types'
import {
  formatDateTime,
  getOption,
  useOptionsTypeOptions,
  useParticipantOptionStatusOptions,
  useRolesTypeOptions,
  Options as Opts,
} from '../../../utils'
import { adminEventDetailsLoader, adminOptionsLoader } from '../Admin.loader'
import AdminEventDetails from './components/AdminEventDetails'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import ViewButtonGroup from './components/ViewButtonGroup'
import MasterDetailsContent from '../../root/components/MasterDetailsContent'
import { useContextShowStatistics } from '../Admin.Context'
import { Dot } from './components/Dot'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import BooleanRender from './components/BooleanRender'

type Row = Options<true>

function MasterPanelParticipantDetails({ row }: { row: Row }) {
  const { t } = useTranslation('admin')
  const rows = row.stats.contributors_detail
  type InnerRow = (typeof rows)[number]

  const rolesType = useRolesTypeOptions(t)

  const callback = useCallback(async () => Promise.resolve(), [])

  const columns: GridColDef<InnerRow>[] = useMemo(
    () => [
      {
        field: 'is_registered',
        headerName: t('Inscrit'),
        width: 100,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'last_name',
        headerName: t('Nom'),
        width: 150,
        valueGetter: (_value, row) => row.user.last_name,
      },
      {
        field: 'first_name',
        headerName: t('Prénom'),
        width: 150,
        valueGetter: (_value, row) => row.user.first_name,
      },
      {
        field: 'roles',
        headerName: t('Rôles'),
        width: 400,
        valueOptions: rolesType,
        type: 'singleSelect',
        display: 'flex',
        // TODO implement multipleSelectOperators to filter by multiple roles
        // https://mui.com/x/react-data-grid/filtering/customization/#multiple-values-operator
        // filterOperators: multipleSelectOperators,
        valueGetter: (value: ContributorRole[]) =>
          value.map(elem => getOption(rolesType, null, elem)),
        renderCell: params => (
          <Stack direction="row" spacing={1}>
            {(params.value as Opts[]).map(opt => (
              <Chip key={opt.value} label={opt.label} />
            ))}
          </Stack>
        ),
      },
    ],
    [rolesType, t]
  )

  return (
    <MasterDetailsContent callback={callback}>
      <StripedDataGrid
        rows={row.stats.contributors_detail}
        getRowId={(row: InnerRow) => row.user.id}
        columns={columns}
        initialState={initialState}
        count={row.stats.contributors_detail.length}
        paginationMode="client"
        checkboxSelection={false}
      />
    </MasterDetailsContent>
  )
}

const getDetailPanelHeight: DataGridPremiumProps['getDetailPanelHeight'] = () => 'auto'
const getDetailPanelContent: DataGridPremiumProps['getDetailPanelContent'] = params => (
  <MasterPanelParticipantDetails row={params.row as Row} />
)

const initialState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          field: 'optional',
          operator: 'is',
          value: true,
        },
      ],
    },
  },
  sorting: {
    sortModel: [
      {
        field: 'warning',
        sort: 'desc',
      },
      {
        field: 'sub_event_type',
        sort: 'desc',
      },
    ],
  },
  columns: {
    columnVisibilityModel: {
      id: false,
      participants_pending: false,
      participants_cancelled: false,
      participants_checkout_failed: false,
    },
  },
}

function getWarningValues(row: Row) {
  return (
    !!row.stats?.participant_status[ParticipantOptionStatus.WaitingConfirmation] ||
    !!row.stats?.not_registered_contributor_count
  )
}

export default function AdminOptions() {
  const { t } = useTranslation('admin')
  const { eventId } = useParams()
  const navigate = useNavigate()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()

  const { event } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const data = useLoaderData() as LoaderData<ReturnType<typeof adminOptionsLoader>> satisfies
    | Row[]
    | undefined

  const optionsTypeOptions = useOptionsTypeOptions(t)
  const participantOptionStatusOptions = useParticipantOptionStatusOptions(t)

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: 'warning',
        headerName: t('Vigilance'),
        type: 'boolean',
        description: t('Attention requise'),
        renderHeader: () => (
          <Tooltip title={t('Attention requise')}>
            <WarningIcon sx={{ fontSize: '1.5rem' }} color="warning" />
          </Tooltip>
        ),
        width: 70,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_value, row) => getWarningValues(row),
        renderCell: params =>
          params.value ? (
            <Tooltip title={t('Attention requise')}>
              <WarningIcon sx={{ fontSize: '1.5rem' }} color="warning" />
            </Tooltip>
          ) : null,
      },
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const option = params.row
          return (
            <Stack direction="row">
              <Tooltip title={t('Voir tous les participants')}>
                <IconButton
                  color="primary"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`${option.id}`)
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Éditer l'option")}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/admin/events/${eventId}/conditions/${option.id}`)
                  }}
                >
                  <EditNoteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'name',
        headerName: t('Nom'),
        width: 270,
      },
      {
        field: 'type',
        headerName: t('Type'),
        width: 150,
        type: 'singleSelect',
        valueOptions: optionsTypeOptions,
        renderCell: params => {
          const type = getOption(optionsTypeOptions, undefined, params.value as OptionType)
          return (
            <Chip
              sx={theme => ({
                backgroundColor: type?.color,
                color: theme.palette.getContrastText(type?.color ?? 'inherit'),
              })}
              size="small"
              label={type?.label ?? 'NC'}
            />
          )
        },
      },
      {
        field: 'deadline',
        headerName: t('Date limite'),
        width: 150,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: value => (value ? formatDateTime(value) : 'NC'),
      },
      {
        field: 'max_participants',
        headerName: t('Max participants'),
        width: 110,
        type: 'number',
        valueFormatter: value => value ?? 'NC',
        display: 'flex',
        renderCell: params => (
          <Dot backgroundColor={theme => theme.palette.primary.main}>{params.value ?? 'NC'}</Dot>
        ),
      },
      {
        field: 'participants_confirmed',
        headerName: t('Nb confirmés'),
        description: t('Nombre de participants confirmés'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.Confirmed],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={
              participantOptionStatusOptions.find(
                el => el.value === ParticipantOptionStatus.Confirmed
              )?.color
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'participants_waiting_confirmation',
        headerName: t('Nb à confirmer'),
        description: t('Nombre de participants en attente de confirmation'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.WaitingConfirmation],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={theme =>
              !!params.value
                ? theme.palette.warning.main
                : (participantOptionStatusOptions.find(
                    el => el.value === ParticipantOptionStatus.WaitingConfirmation
                  )?.color ?? 'inherit')
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'participants_waiting_list',
        headerName: t('Nb attente'),
        description: t('Nombre de participants en liste d’attente'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.WaitingList],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={
              participantOptionStatusOptions.find(
                el => el.value === ParticipantOptionStatus.WaitingList
              )?.color
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'expected_contributors',
        headerName: t('Co attendus'),
        description: t('Nombre de contributeurs attendus'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) => row.stats?.expected_contributors,
        display: 'flex',
        renderCell: params => (
          <Dot backgroundColor={theme => theme.palette.primary.main}>{params.value ?? 'NC'}</Dot>
        ),
      },
      {
        field: 'not_registered_contributor_count',
        headerName: t('Co non-inscrits'),
        description: t('Nombre de contributeurs non-inscrits'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) => row.stats?.not_registered_contributor_count,
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={theme =>
              !!params.value ? theme.palette.warning.main : theme.palette.primary.dark
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'contributor_count',
        headerName: t('Nb contributeurs'),
        description: t('Nombre de contributeurs'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) => row.stats?.contributor_count,
        display: 'flex',
        renderCell: params => (
          <Dot backgroundColor={theme => theme.palette.primary.dark}>{params.value ?? 'NC'}</Dot>
        ),
      },
      {
        field: 'participants_pending',
        headerName: t('Nb paie attente'),
        description: t('Nombre de participants en attente de paiement'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.PendingCheckout],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={
              participantOptionStatusOptions.find(
                el => el.value === ParticipantOptionStatus.PendingCheckout
              )?.color
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'participants_cancelled',
        headerName: t('Nb annulés'),
        description: t('Nombre de participants annulés'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.Cancelled],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={
              participantOptionStatusOptions.find(
                el => el.value === ParticipantOptionStatus.Cancelled
              )?.color
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
      {
        field: 'participants_checkout_failed',
        headerName: t('Nb paie échoué'),
        description: t('Nombre de participants avec paiement échoué'),
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          row.stats?.participant_status[ParticipantOptionStatus.CheckoutFailed],
        display: 'flex',
        renderCell: params => (
          <Dot
            backgroundColor={
              participantOptionStatusOptions.find(
                el => el.value === ParticipantOptionStatus.CheckoutFailed
              )?.color
            }
          >
            {params.value ?? 'NC'}
          </Dot>
        ),
      },
    ],
    [t, optionsTypeOptions, navigate, eventId, participantOptionStatusOptions]
  )

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails événement')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminEventDetails event={event} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
        >
          <h3>{t('Liste des options')} :</h3>
          <ViewButtonGroup />
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            rows={data}
            columns={columns}
            initialState={initialState}
            getRowClassName={params => {
              const warningValue = getWarningValues(params.row as Row)
              return !!warningValue ? 'warning' : ''
            }}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            count={data?.length}
            paginationMode="client"
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
